/**
 * KK-102 / KK-103 / KK-105 / KK-107 · Kart yönetimi.
 *
 * EKRANIN TAŞIDIĞI TEK BÜYÜK FİKİR (AK-07 / ADR-04):
 *   Cüzdan KİŞİYE aittir, karta değil. Kart kaybında bakiye KİŞİDE KALIR ve
 *   "bakiye transferi" diye bir işlem YOKTUR. Bu, arayüzde gizlenmesi gereken
 *   bir ayrıntı değil; velinin ve görevlinin panik anındaki tek sorusunun
 *   ("param ne oldu?") cevabıdır. Bu yüzden ekranda kalıcı olarak yazılıdır.
 */
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alan, Avatar, Buton, Kart, Rozet, Tutar, Uyari } from "@kantin/ui";
import { CuzdanTuru, KartDurumu } from "@kantin/shared";
import { apiOku, apiYaz } from "./api.js";
import {
  AramaKutusu,
  IslemDurumu,
  MetinAlani,
  Sayfa,
  SayfaBasligi,
  SorguDurumu,
  paraGecerliMi,
  paraNormalize,
  tarihSaatGoster,
} from "./ortak.js";

interface KisiSatiri {
  id: string;
  ad: string;
  soyad: string;
  sinif: string | null;
  numara: string | null;
  aktif: boolean;
  yukleme_referansi: string;
  bakiye: string;
  aktif_kart_uid: string | null;
  /** Aktif kart yok ama bloke kart var: "kart yok" değil "kart bloke". */
  bloke_kart_var: boolean;
}

interface KisiDetayi {
  id: string;
  ad: string;
  soyad: string;
  sinif: string | null;
  foto_url: string | null;
  aktif: boolean;
  yukleme_referansi: string;
  cuzdanlar: { id: string; tur: number; bakiye: string }[];
  kartlar: {
    id: string;
    uid: string;
    durum: number;
    depozito: string;
    bloke_zamani: string | null;
    bloke_sebebi: string | null;
    teslim_zamani: string | null;
  }[];
}

const KART_DURUM_ADI: Record<number, string> = {
  [KartDurumu.AKTIF]: "Aktif",
  [KartDurumu.BLOKE]: "Bloke",
  [KartDurumu.IADE]: "İade edildi",
  [KartDurumu.HAVUZDA]: "Havuzda",
};

export function Kartlar() {
  const [arama, setArama] = useState("");
  const [seciliKisi, setSeciliKisi] = useState<string | null>(null);

  const liste = useQuery({
    queryKey: ["kart-kisi-arama", arama],
    queryFn: () =>
      apiOku<{ veri: KisiSatiri[]; toplam: number }>("/kisi", {
        arama: arama || undefined,
        boyut: 20,
        sayfa: 1,
      }),
    enabled: arama.trim().length > 0,
  });

  return (
    <Sayfa>
      <SayfaBasligi
        baslik="Kartlar"
        aciklama="Kart eşleştirme, eşleşmeyi kaldırma, blokaj ve kayıp kart yenileme. Eşleşmesi kaldırılan kart başka bir kişiye atanabilir."
      />

      {/* AK-07 — ekranın en üstünde, kalıcı. */}
      <Uyari tur="bilgi" baslik="Kart kaybında bakiye kişidedir">
        <p className="m-0">
          Cüzdan kişiye aittir, karta değil. Kart bloke edildiğinde{" "}
          <strong>tek bir bakiye hareketi bile yazılmaz</strong>; para olduğu
          yerde kalır. Yeni kart eşleştirildiğinde bakiye &quot;geri
          gelmez&quot;, zaten hiç gitmemiştir.
        </p>
        <p className="m-0 mt-1">
          Bu yüzden sistemde{" "}
          <strong>bakiye transferi diye bir işlem yoktur</strong> — olsaydı her
          kart kaybında para taşıma hatası ihtimali doğardı.
        </p>
      </Uyari>

      <Kart baslik="Kişi bul">
        <div className="grid gap-3 md:grid-cols-2">
          <AramaKutusu
            etiket="Ad, numara veya referans kodu"
            ipucu="Ayşe Yılmaz / 1042 / KNT-4821"
            deger={arama}
            degistir={setArama}
          />
        </div>

        {arama.trim().length === 0 ? (
          <p className="m-0 mt-3 text-sm text-murekkep-3">
            İşlem yapmak için önce kişiyi arayın.
          </p>
        ) : (
          <div className="mt-3">
            <SorguDurumu
              yukleniyor={liste.isPending}
              hata={liste.error}
              bos={(liste.data?.veri.length ?? 0) === 0}
              bosBaslik="Kişi bulunamadı"
              bosAciklama="Farklı bir ad, numara veya referans kodu deneyin."
              iskeletSatir={3}
              yenile={() => void liste.refetch()}
            >
              <ul className="m-0 flex list-none flex-col gap-1 p-0">
                {(liste.data?.veri ?? []).map((k) => (
                  <li key={k.id}>
                    <button
                      type="button"
                      onClick={() => setSeciliKisi(k.id)}
                      aria-pressed={seciliKisi === k.id}
                      className={`flex min-h-hedef w-full items-center gap-3 rounded-md border px-3 py-2 text-left ${
                        seciliKisi === k.id
                          ? "border-simit bg-simit-yumusak"
                          : "border-cetvel bg-yuzey hover:bg-kagit-2"
                      }`}
                    >
                      <Avatar ad={k.ad} soyad={k.soyad} boyut={32} />
                      <span className="min-w-0 flex-1">
                        <span className="block font-semibold text-murekkep">
                          {k.ad} {k.soyad}
                        </span>
                        <span className="block text-xs text-murekkep-3">
                          {k.sinif ?? "—"}
                          {k.numara ? ` · No ${k.numara}` : ""} ·{" "}
                          {k.aktif_kart_uid
                            ? `kart ${k.aktif_kart_uid}`
                            : k.bloke_kart_var
                              ? "kart bloke"
                              : "kart yok"}
                        </span>
                      </span>
                      <Tutar deger={k.bakiye} yonsuz boy="sm" etiket="bakiye" />
                    </button>
                  </li>
                ))}
              </ul>
            </SorguDurumu>
          </div>
        )}
      </Kart>

      {seciliKisi && <KartIslemleri key={seciliKisi} kisiId={seciliKisi} />}
    </Sayfa>
  );
}

// ---------------------------------------------------------------------------

function KartIslemleri({ kisiId }: { kisiId: string }) {
  const istemci = useQueryClient();
  const sorgu = useQuery({
    queryKey: ["kisi", kisiId],
    queryFn: () => apiOku<KisiDetayi>(`/kisi/${kisiId}`),
  });
  const [islem, setIslem] = useState<"eslestir" | "yenile">("eslestir");
  const [kaldirilanUid, setKaldirilanUid] = useState<string | null>(null);

  const tazele = () => {
    void istemci.invalidateQueries({ queryKey: ["kisi"] });
    void istemci.invalidateQueries({ queryKey: ["kart-kisi-arama"] });
  };

  const k = sorgu.data;
  const kantinBakiyesi =
    k?.cuzdanlar.find((c) => c.tur === CuzdanTuru.KANTIN)?.bakiye ?? "0.00";
  const aktifKart = k?.kartlar.find((x) => x.durum === KartDurumu.AKTIF);

  return (
    <Kart baslik="Kart işlemleri">
      <SorguDurumu
        yukleniyor={sorgu.isPending}
        hata={sorgu.error}
        bos={!k}
        bosBaslik="Kişi bulunamadı"
        yenile={() => void sorgu.refetch()}
      >
        {k && (
          <div className="flex flex-col gap-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Avatar
                  ad={k.ad}
                  soyad={k.soyad}
                  fotoUrl={k.foto_url}
                  boyut={56}
                />
                <div>
                  <h3 className="m-0 text-lg">
                    {k.ad} {k.soyad}
                  </h3>
                  <p className="m-0 text-sm text-murekkep-3">
                    {k.sinif ?? "—"} · referans{" "}
                    <span className="rakam font-mono">
                      {k.yukleme_referansi}
                    </span>
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="m-0 text-xs text-murekkep-3">Kantin bakiyesi</p>
                <Tutar
                  deger={kantinBakiyesi}
                  yonsuz
                  boy="xl"
                  etiket="kantin bakiyesi"
                />
              </div>
            </div>

            {!k.aktif && (
              <Uyari tur="uyari" baslik="Bu kişi pasif">
                Pasif kişiye kart eşleştirilemez. Önce Kişiler ekranından aktif
                edin.
              </Uyari>
            )}

            <section aria-label="Mevcut kartlar">
              <h4 className="m-0 mb-2 text-sm font-semibold text-murekkep-3">
                Mevcut kartlar
              </h4>
              {k.kartlar.length === 0 ? (
                <p className="m-0 text-sm text-murekkep-3">
                  Bu kişiye atanmış kart yok.
                </p>
              ) : (
                <ul className="m-0 flex list-none flex-col gap-2 p-0">
                  {k.kartlar.map((kt) => (
                    <KartSatiri
                      key={kt.id}
                      kart={kt}
                      kisiId={k.id}
                      tazele={tazele}
                      kaldirildi={() => setKaldirilanUid(kt.uid)}
                    />
                  ))}
                </ul>
              )}
            </section>

            {kaldirilanUid && (
              <Uyari tur="onay" baslik="Kart eşleşmesi kaldırıldı">
                {kaldirilanUid} başka bir kişiye eşleştirilebilir.
                Kişi aktifliği ve kantin bakiyesi değişmedi.
              </Uyari>
            )}

            <div className="border-t border-cetvel pt-4">
              <div className="mb-3 flex flex-wrap gap-2">
                <Buton
                  tur={islem === "eslestir" ? "birincil" : "ikincil"}
                  onClick={() => setIslem("eslestir")}
                  aria-pressed={islem === "eslestir"}
                >
                  Kart eşleştir
                </Buton>
                <Buton
                  tur={islem === "yenile" ? "birincil" : "ikincil"}
                  onClick={() => setIslem("yenile")}
                  aria-pressed={islem === "yenile"}
                >
                  Kayıp kart yenile
                </Buton>
              </div>

              {islem === "eslestir" ? (
                <EslestirmeFormu
                  kisiId={k.id}
                  aktifKartVarMi={Boolean(aktifKart)}
                  tazele={tazele}
                />
              ) : (
                <YenilemeFormu kisiId={k.id} tazele={tazele} />
              )}
            </div>
          </div>
        )}
      </SorguDurumu>
    </Kart>
  );
}

// ---------------------------------------------------------------------------

function KartSatiri({
  kart,
  kisiId,
  tazele,
  kaldirildi,
}: {
  kart: KisiDetayi["kartlar"][number];
  kisiId: string;
  tazele: () => void;
  kaldirildi: () => void;
}) {
  const [blokeAcik, setBlokeAcik] = useState(false);
  const [kaldirAcik, setKaldirAcik] = useState(false);
  const [sebep, setSebep] = useState("");
  const [gonderildi, setGonderildi] = useState(false);

  const blokeEt = useMutation({
    mutationFn: () =>
      apiYaz("POST", `/kart/${kart.id}/bloke`, { sebep: sebep.trim() }),
    onSuccess: () => {
      setBlokeAcik(false);
      setSebep("");
      setGonderildi(false);
      tazele();
    },
  });

  const aktifEt = useMutation({
    mutationFn: () => apiYaz("POST", `/kart/${kart.id}/aktif`),
    onSuccess: tazele,
  });

  const kaldir = useMutation({
    mutationFn: () =>
      apiYaz("POST", `/kart/${kart.id}/eslesme-kaldir`, { kisi_id: kisiId }),
    onSuccess: () => {
      kaldirildi();
      tazele();
    },
  });
  const bekliyor = kaldir.isPending || blokeEt.isPending || aktifEt.isPending;

  const sebepHatasi =
    gonderildi && sebep.trim().length < 3
      ? "Blokaj sebebi en az 3 karakter olmalıdır."
      : undefined;

  return (
    <li className="rounded-md border border-cetvel px-3 py-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <span className="rakam font-mono text-sm">{kart.uid}</span>
          <span className="ml-2">
            <Rozet
              tur={
                kart.durum === KartDurumu.AKTIF
                  ? "onay"
                  : kart.durum === KartDurumu.BLOKE
                    ? "hata"
                    : "notr"
              }
            >
              {KART_DURUM_ADI[kart.durum] ?? "Bilinmiyor"}
            </Rozet>
          </span>
          {kart.bloke_zamani && (
            <p className="m-0 text-xs text-murekkep-3">
              {tarihSaatGoster(kart.bloke_zamani)}
              {kart.bloke_sebebi ? ` · ${kart.bloke_sebebi}` : ""}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-murekkep-3">
            Depozito <Tutar deger={kart.depozito} yonsuz boy="sm" />
          </span>
          {kart.durum === KartDurumu.AKTIF && (
            <Buton
              tur="tehlike"
              onClick={() => {
                setBlokeAcik((a) => !a);
                setKaldirAcik(false);
              }}
              disabled={bekliyor}
              aria-expanded={blokeAcik}
            >
              Bloke et
            </Buton>
          )}
          {kart.durum === KartDurumu.BLOKE && (
            <Buton
              tur="ikincil"
              onClick={() => aktifEt.mutate()}
              yukleniyor={aktifEt.isPending}
              disabled={bekliyor}
            >
              Blokeyi kaldır (kart bulundu)
            </Buton>
          )}
          <Buton
            tur="tehlike"
            disabled={bekliyor}
            aria-expanded={kaldirAcik}
            onClick={() => {
              setKaldirAcik((a) => !a);
              setBlokeAcik(false);
            }}
          >
            Eşleşmeyi kaldır
          </Buton>
        </div>
      </div>

      {kaldirAcik && (
        <div className="mt-3 flex flex-col gap-3 rounded-lg border border-hata-cizgi bg-hata-yumusak p-3">
          <p className="m-0 text-sm">
            <strong>{kart.uid}</strong> kartının bu kişiyle eşleşmesi kaldırılacak.
            Kişi aktif kalır, kantin bakiyesi değişmez. Kart başka bir kişiye atanabilir.
            Yalnızca geri teslim alınan kartlar için bu işlemi kullanın.
            Varsa kart depozitosu iade olarak kaydedilir; geri ödemeyi kişiye teslim edin.
          </p>
          <IslemDurumu hata={kaldir.error} />
          <div className="flex gap-2">
            <Buton
              tur="tehlike"
              yukleniyor={kaldir.isPending}
              disabled={bekliyor}
              onClick={() => kaldir.mutate()}
            >
              Evet, eşleşmeyi kaldır
            </Buton>
            <Buton
              tur="sessiz"
              disabled={bekliyor}
              onClick={() => setKaldirAcik(false)}
            >
              Vazgeç
            </Buton>
          </div>
        </div>
      )}

      {blokeAcik && (
        <form
          className="mt-3 flex flex-col gap-2 rounded-lg border border-hata-cizgi bg-hata-yumusak p-3"
          onSubmit={(e) => {
            e.preventDefault();
            setGonderildi(true);
            if (sebep.trim().length < 3) return;
            blokeEt.mutate();
          }}
        >
          <p className="m-0 text-sm text-murekkep-2">
            Blokaj bir <strong>kimlik işlemidir, para işlemi değil</strong>.
            Bakiye kişinin cüzdanında kalır.
          </p>
          <MetinAlani
            etiket="Blokaj sebebi"
            value={sebep}
            onChange={(e) => setSebep(e.target.value)}
            rows={2}
            ipucu="Örnek: Veli kartın kaybedildiğini bildirdi."
            {...(sebepHatasi ? { hata: sebepHatasi } : {})}
          />
          <IslemDurumu hata={blokeEt.error} />
          <div className="flex gap-2">
            <Buton type="submit" tur="tehlike" yukleniyor={blokeEt.isPending}>
              Kartı bloke et
            </Buton>
            <Buton
              type="button"
              tur="ikincil"
              onClick={() => setBlokeAcik(false)}
            >
              Vazgeç
            </Buton>
          </div>
        </form>
      )}

      <IslemDurumu hata={aktifEt.error} />
    </li>
  );
}

// ---------------------------------------------------------------------------

function UidAlani({
  etiket,
  deger,
  degistir,
  hata,
}: {
  etiket: string;
  deger: string;
  degistir: (yeni: string) => void;
  hata?: string;
}) {
  const alan = useRef<HTMLInputElement>(null);
  return (
    <div className="flex items-end gap-2">
      <div className="flex-1">
        <Alan
          ref={alan}
          etiket={etiket}
          value={deger}
          onChange={(e) => degistir(e.target.value.toUpperCase())}
          ipucu="Okuyucuya kartı okutun (alan seçili olmalı) veya UID'i elle yazın."
          inputMode="text"
          autoComplete="off"
          spellCheck={false}
          className="font-mono"
          {...(hata ? { hata } : {})}
        />
      </div>
      <Buton
        type="button"
        tur="ikincil"
        onClick={() => alan.current?.focus()}
        title="Okuyucudan okutabilmek için alanı seçer"
      >
        Okuyucudan oku
      </Buton>
    </div>
  );
}

function EslestirmeFormu({
  kisiId,
  aktifKartVarMi,
  tazele,
}: {
  kisiId: string;
  aktifKartVarMi: boolean;
  tazele: () => void;
}) {
  const [uid, setUid] = useState("");
  const [depozito, setDepozito] = useState("");
  const [gonderildi, setGonderildi] = useState(false);

  const eslestir = useMutation({
    mutationFn: () =>
      apiYaz<{
        id: string;
        uid: string;
        bloke_edilen_onceki_kart_id: string | null;
      }>("POST", "/kart/eslesme", {
        kisi_id: kisiId,
        uid: uid.trim(),
        ...(depozito.trim() ? { depozito: paraNormalize(depozito) } : {}),
      }),
    onSuccess: () => {
      setUid("");
      setDepozito("");
      setGonderildi(false);
      tazele();
    },
  });

  const uidHatasi =
    gonderildi && uid.trim().length < 4
      ? "Kart UID en az 4 karakter olmalıdır."
      : undefined;
  const depozitoHatasi =
    depozito.trim() && !paraGecerliMi(depozito)
      ? "Tutarı 12,50 biçiminde girin."
      : undefined;

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        setGonderildi(true);
        if (uid.trim().length < 4 || depozitoHatasi) return;
        eslestir.mutate();
      }}
    >
      {aktifKartVarMi && (
        <Uyari tur="uyari" baslik="Bu kişinin zaten aktif bir kartı var">
          Yeni kart eşleştirildiğinde eski kart otomatik bloke edilir. Bir
          kişinin aynı anda yalnızca bir aktif kartı olabilir.
        </Uyari>
      )}

      <UidAlani
        etiket="Kart UID"
        deger={uid}
        degistir={setUid}
        {...(uidHatasi ? { hata: uidHatasi } : {})}
      />

      <div className="max-w-xs">
        <Alan
          etiket="Depozito (isteğe bağlı)"
          value={depozito}
          onChange={(e) => setDepozito(e.target.value)}
          inputMode="decimal"
          ipucu="Alındıysa ayrı bir depozito hesabına yazılır; kantin bakiyesine karışmaz."
          {...(depozitoHatasi ? { hata: depozitoHatasi } : {})}
        />
      </div>

      <IslemDurumu hata={eslestir.error} />
      {eslestir.data && (
        <Uyari tur="onay" baslik="Kart eşleştirildi">
          <span className="rakam font-mono">{eslestir.data.uid}</span>
          {eslestir.data.bloke_edilen_onceki_kart_id
            ? " · önceki kart bloke edildi"
            : ""}
        </Uyari>
      )}

      <div>
        <Buton type="submit" tur="birincil" yukleniyor={eslestir.isPending}>
          Kartı eşleştir
        </Buton>
      </div>
    </form>
  );
}

function YenilemeFormu({
  kisiId,
  tazele,
}: {
  kisiId: string;
  tazele: () => void;
}) {
  const [uid, setUid] = useState("");
  const [depozito, setDepozito] = useState("");
  const [gonderildi, setGonderildi] = useState(false);

  const yenile = useMutation({
    mutationFn: () =>
      apiYaz<{
        id: string;
        uid: string;
        tahsil_edilen_ucret: string;
        bloke_edilen_onceki_kart_id: string | null;
      }>("POST", "/kart/yenile", {
        kisi_id: kisiId,
        yeni_uid: uid.trim(),
        ...(depozito.trim() ? { depozito: paraNormalize(depozito) } : {}),
      }),
    onSuccess: () => {
      setUid("");
      setDepozito("");
      setGonderildi(false);
      tazele();
    },
  });

  const uidHatasi =
    gonderildi && uid.trim().length < 4
      ? "Kart UID en az 4 karakter olmalıdır."
      : undefined;
  const depozitoHatasi =
    depozito.trim() && !paraGecerliMi(depozito)
      ? "Tutarı 12,50 biçiminde girin."
      : undefined;

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        setGonderildi(true);
        if (uid.trim().length < 4 || depozitoHatasi) return;
        yenile.mutate();
      }}
    >
      <Uyari tur="uyari" baslik="Yenileme ücreti bakiyeden tahsil edilir">
        <p className="m-0">
          Okul ayarlarındaki kart yenileme ücreti kişinin kantin bakiyesinden
          düşülür. Bakiye yetersizse işlem <strong>hiç başlamaz</strong> — kartı
          verip ücreti sonra almak, mutabakatta açıklanamayan bir fark üretir.
        </p>
        <p className="m-0 mt-1">
          Eski kart bloke edilir. <strong>Bakiye transferi yapılmaz</strong>;
          para zaten kişinin cüzdanındadır.
        </p>
      </Uyari>

      <UidAlani
        etiket="Yeni kart UID"
        deger={uid}
        degistir={setUid}
        {...(uidHatasi ? { hata: uidHatasi } : {})}
      />

      <div className="max-w-xs">
        <Alan
          etiket="Depozito (isteğe bağlı)"
          value={depozito}
          onChange={(e) => setDepozito(e.target.value)}
          inputMode="decimal"
          {...(depozitoHatasi ? { hata: depozitoHatasi } : {})}
        />
      </div>

      <IslemDurumu hata={yenile.error} />
      {yenile.data && (
        <Uyari tur="onay" baslik="Kart yenilendi">
          Yeni UID: <span className="rakam font-mono">{yenile.data.uid}</span> ·
          Tahsil edilen ücret:{" "}
          <Tutar
            deger={yenile.data.tahsil_edilen_ucret}
            yonsuz
            boy="sm"
            etiket="tahsil edilen ücret"
          />
        </Uyari>
      )}

      <div>
        <Buton type="submit" tur="birincil" yukleniyor={yenile.isPending}>
          Kartı yenile
        </Buton>
      </div>
    </form>
  );
}
